import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isApiError } from '@missions/api-client';
import { type Me, type Permission, type TenantPublicConfig, type Terms, terms as TERMS } from '@missions/types';
import { useBrand } from '@missions/ui';
import { api, onUnauthenticated } from './client';

type Status = 'loading' | 'signed-in' | 'signed-out';

interface SessionApi {
  status: Status;
  me: Me | null;
  tenant: TenantPublicConfig;
  terms: Terms;
  can(p: Permission): boolean;
  /** Owner/Admin-only areas (admin notifications, billing…) */
  isAdmin: boolean;
  signIn(email: string, password: string, remember: boolean): Promise<void>;
  signOut(): Promise<void>;
  /** After claim/accept-invite: the API already returned a session */
  adopt(accessToken: string, me: Me): void;
  setMe(me: Me): void;
  refetchMe(): Promise<void>;
  /** After Settings → Branding saves: update logo/title/etc. in the running app without a reload. */
  patchTenant(p: Partial<TenantPublicConfig>): void;
  /** "Session expired" screen (refresh failed while signed in) */
  expired: boolean;
}

const Ctx = createContext<SessionApi | null>(null);

export function useSession(): SessionApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSession() must be used inside <SessionProvider>');
  return c;
}

/** Wording that follows the tenant's Mission language setting (live-updates when Branding changes it). */
export function useT(): Terms {
  const brand = useBrand();
  return TERMS.for(brand.missionLanguage);
}

export function SessionProvider({ tenant: initialTenant, children }: { tenant: TenantPublicConfig; children: ReactNode }) {
  const qc = useQueryClient();
  const [tenant, setTenant] = useState(initialTenant);
  const patchTenant = useCallback((p: Partial<TenantPublicConfig>) => setTenant((t) => ({ ...t, ...p })), []);
  const brand = useBrand();
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMeState] = useState<Me | null>(null);
  const [expired, setExpired] = useState(false);

  const setMe = useCallback(
    (m: Me) => {
      setMeState(m);
      // a user's own theme preference wins over the tenant default (stored locally by BrandProvider)
      try {
        if (!localStorage.getItem('ui-theme')) brand.setTheme(m.user.themeMode);
      } catch {
        /* private mode */
      }
    },
    [brand],
  );

  const refetchMe = useCallback(async () => {
    const m = await api.call('me.get');
    setMe(m);
  }, [setMe]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // "Enter as DevNinjaWeb" (Mission HQ): #staff_token=<access token>. The fragment never reaches servers;
        // keep it in memory only (15 min, no refresh cookie) and strip it from the address bar right away.
        const staff = /[#&]staff_token=([^&]+)/.exec(window.location.hash)?.[1];
        if (staff) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
          api.setAccessToken(decodeURIComponent(staff));
          const m = await api.call('me.get');
          if (!alive) return;
          setMe(m);
          setStatus('signed-in');
          return;
        }
        const ok = await api.refresh();
        if (!alive) return;
        if (!ok) {
          setStatus('signed-out');
          return;
        }
        const m = await api.call('me.get');
        if (!alive) return;
        setMe(m);
        setStatus('signed-in');
      } catch {
        if (alive) setStatus('signed-out');
      }
    })();
    return () => {
      alive = false;
    };
    // boot once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () =>
      onUnauthenticated(() => {
        setStatus((s) => {
          if (s === 'signed-in') setExpired(true);
          return 'signed-out';
        });
      }),
    [],
  );

  const value = useMemo<SessionApi>(() => {
    const perms = new Set<string>(me?.permissions ?? []);
    const roleKey = me?.role.key ?? '';
    return {
      status,
      me,
      tenant,
      terms: TERMS.for(brand.missionLanguage),
      expired,
      can: (p) => perms.has(p),
      isAdmin: roleKey === 'owner' || roleKey === 'admin',
      async signIn(email, password, remember) {
        const s = await api.call('auth.login', { body: { email, password, remember } });
        api.setAccessToken(s.accessToken);
        setMe(s.me);
        setExpired(false);
        setStatus('signed-in');
      },
      async signOut() {
        try {
          // staff sessions have no refresh cookie: just drop the in-memory token
          if (!me?.impersonatedBy) await api.call('auth.logout');
        } catch (e) {
          if (!isApiError(e)) throw e;
        }
        api.setAccessToken(null);
        qc.clear();
        setMeState(null);
        setStatus('signed-out');
      },
      adopt(accessToken, m) {
        api.setAccessToken(accessToken);
        setMe(m);
        setExpired(false);
        setStatus('signed-in');
      },
      setMe,
      refetchMe,
      patchTenant,
    };
  }, [status, me, tenant, brand.missionLanguage, expired, qc, setMe, refetchMe, patchTenant]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
